/** Browser acceptance safety policy, not an API guarantee or a shared server quota.
 * Mirrors the portable runner's proposed ordinary-run limits; Hal must confirm
 * live allocation before enablement. Reconnect/reload must not reset the window.
 */
export const CFB_BROWSER_LIMITS = { requests: 80, durationMs: 180000, pollMs: 5000, requestTimeoutMs: 15000 } as const;
export interface BudgetStorage { getItem(key: string): string | null; setItem(key: string, value: string): void; }
interface BudgetState { startedAt: number; sends: number; stopped?: string; }
export class CfbBudget {
  constructor(private storage: BudgetStorage, private key: string, private now = () => Date.now()) {}
  private read(): BudgetState | undefined {
    const raw = this.storage.getItem(this.key);
    if (!raw) return undefined;
    const state = JSON.parse(raw);
    if (!Number.isFinite(state.startedAt) || !Number.isSafeInteger(state.sends) || state.sends < 0) throw new Error('Invalid saved browser request budget. Operator review required.');
    return state;
  }
  get reason(): string {
    try {
      const state = this.read();
      if (!state) return '';
      if (state.stopped) return state.stopped;
      if (state.sends >= CFB_BROWSER_LIMITS.requests) return 'Browser request limit reached (80).';
      if (this.now() - state.startedAt >= CFB_BROWSER_LIMITS.durationMs || this.now() < state.startedAt) return 'Browser time limit reached (180 seconds).';
      return '';
    } catch { return 'Saved browser budget is unavailable. Operator review required.'; }
  }
  get remainingMs(): number {
    const state = this.read();
    return state ? Math.max(0, CFB_BROWSER_LIMITS.durationMs - (this.now() - state.startedAt)) : CFB_BROWSER_LIMITS.durationMs;
  }
  claim() {
    if (this.reason) throw new Error(this.reason);
    const state = this.read() || { startedAt: this.now(), sends: 0 };
    state.sends++;
    // Persist before sending; failures cannot silently turn off the safety limit.
    this.storage.setItem(this.key, JSON.stringify(state));
  }
  stop(reason: string) {
    const state = this.read() || { startedAt: this.now(), sends: 0 };
    this.storage.setItem(this.key, JSON.stringify({ ...state, stopped: reason }));
  }
}
